<template>
    <div style="background-color: #e8e8e8">
        <!-- Login view -->
        <login v-if="!loggedIn && !passwordReset" ref="login" :external-message="loginMessage"
               @reset-password="passwordReset = true" @close-login-form="loggedIn = true" />
        <!-- Reset password view -->
        <reset-password-request v-if="!loggedIn && passwordReset" @reset-password-done="resetPasswordDone"
                                @reset-password-close="passwordReset = false" />


        <div v-if="loggedIn" class="col-12 vh-100">
            <!-- Sidebar menu -->
            <side-menu v-if="menuItems.length > 0" ref="menu" :menu-items="menuItems" :chat-enabled="isChatEnabled" :active-tool="activeTool" @item-click="setActiveView" @menu-changed="onMenuCollapsed" @logout-clicked="logout" @tool-changed="onToolChanged" />
            <!-- Right toolbar -->
            <tool-bar :visible="menuItems.length > 0" :chat-enabled="isChatEnabled" :active-tool="activeTool" @tool-changed="onToolChanged" />
            <!-- Chat panel -->
            <ai-chat-panel v-if="isChatEnabled && activeTool === 'chat'" ref="activePanel" @close="onToolClose" />
            <!-- Help panel -->
            <help-panel v-if="activeTool === 'help'" ref="activePanel" @close="onToolClose" />
            <!-- About panel -->
            <about-panel v-if="activeTool === 'about'" ref="activePanel" @close="onToolClose" />
            <!-- Favorites panel -->
            <favorites-panel v-if="activeTool === 'favorites'" ref="activePanel" @close="onToolClose" @shortcut-clicked="openShortcut" />
            <!-- Main Content (right side) -->
            <div v-show="appForeground" class="routa-content" :class="{'large-content' : menuCollapsed, 'full-content' : menuHidden}">
                <Transition name="fade">
                    <map-view v-if="activeView === 'map'" :user="user" :is-observer="isObserver" :is-worker="isWorker" :is-map-viewer="isMapViewer" @start-inspection="startInspection" />
                </Transition>
                <Transition name="fade">
                    <route-optimization-workflow v-if="activeView === 'routeOptimization'" :user="user"/>
                </Transition>
                <Transition name="fade">
                    <video-recordings v-if="activeView === 'videoRecordings'" :user="user"/>
                </Transition>
                <Transition name="fade">
                    <trips v-if="activeView === 'searchTrips'" :user="user" :is-worker="isWorker"
                           :is-map-viewer="isMapViewer" :is-observer="isObserver" :pre-selected-contract="shortcutItem"/>
                </Transition>
                <Transition name="fade">
                    <contract-trip-summaries v-if="activeView === 'tripReports'" :user="user" :is-worker="isWorker"
                                             :is-map-viewer="isMapViewer" :is-observer="isObserver" :pre-selected-contract="shortcutItem"/>
                </Transition>
                <Transition name="fade">
                    <road-reports v-if="activeView === 'roadReports'" :is-observer="isObserver" />
                </Transition>
                <Transition>
                    <fuel-report v-if="activeView === 'fuelReport'" :is-observer="isObserver"/>
                </Transition>
                <Transition name="fade">
                    <deliveries v-if="activeView === 'deliveries'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <material-reports v-if="activeView === 'materialReports'" :user="user" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <spreader-data-tabbed v-if="activeView === 'spreaderData'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <observations-videos v-if="activeView === 'observationsVideos'" :is-observer="isObserver" :user="user" />
                </Transition>
                <Transition name="fade">
                    <observations v-if="activeView === 'observations'" :user="user" :is-map-viewer="isMapViewer" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <observation-reports v-if="activeView === 'observationReports'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <street-lights-view v-if="activeView === 'streetLights'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <mixed-waste-bins v-if="activeView === 'mixedWasteBins'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <observation-reports v-if="activeView === 'observationReport'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <danger-reports v-if="activeView === 'dangerReports'" :is-observer="isObserver" :user="user" />
                </Transition>
                <Transition name="fade">
                    <user-alerts v-if="activeView === 'userAlerts'" :is-observer="isObserver"  />
                </Transition>
                <Transition name="fade">
                    <inspections-view v-if="activeView === 'inspections'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <mvr-inspections-view v-if="activeView === 'mvrInspections'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <contractor-reports v-if="activeView === 'contractorReports'" />
                </Transition>
                <Transition name="fade">
                    <contract-user-reports v-if="activeView === 'contractUserReports'" />
                </Transition>
                <Transition name="fade">
                    <subcontractor-applications v-if="activeView === 'subcontractorApprovals'" />
                </Transition>
                <Transition name="fade">
                    <general-guides v-if="activeView === 'generalGuides'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <storm-sewers v-if="activeView === 'sewers'" :user="user"/>
                </Transition>
                <Transition name="fade">
                    <road-signs v-if="activeView === 'roadSigns'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <work-reports v-if="activeView === 'workReports'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <work-permissions v-if="activeView === 'workPermissions'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <dispatch v-if="activeView === 'dispatch'" />
                </Transition>
                <Transition name="fade">
                    <work-assignments v-if="activeView ==='workAssignments'" :is-observer="isObserver" :is-admin="isAdmin" :is-map-viewer="isMapViewer" :user="user" />
                </Transition>
                <Transition name="fade">
                    <material-storage-search v-if="activeView === 'materialStorageSearch'" :user="user" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <bridges v-if="activeView === 'bridges'" @start-inspection="startInspection" />
                </Transition>
                <Transition name="fade">
                    <culverts v-if="activeView === 'culverts'"/>
                </Transition>
                <Transition name="fade">
                    <area-groups v-if="activeView === 'areaGroups'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <area-work v-if="activeView === 'areaWork'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <material-stations v-if="activeView === 'materialStations'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <material-delivery-notes v-if="activeView === 'deliveryNotes'" :user="user" :is-observer="isObserver" :login-time="loginTime" />
                </Transition>
                <Transition name="fade">
                    <external-scale-records v-if="activeView === 'unknownNotes'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <notification-lists v-if="activeView === 'notificationLists'" />
                </Transition>
                <Transition name="fade">
                    <data-importer v-if="activeView === 'dataImport'" />
                </Transition>
                <Transition name="fade">
                    <task-types v-if="activeView === 'taskTypes'"/>
                </Transition>
                <Transition name="fade">
                    <price-lists v-if="activeView === 'priceLists'" />
                </Transition>
                <Transition name="fade">
                    <company-defaults v-if="activeView === 'companyDefaults'" />
                </Transition>
                <Transition name="fade">
                    <conversions v-if="activeView === 'conversions'" />
                </Transition>
                <Transition name="fade">
                    <cost-centers v-if="activeView === 'costCenter'" :show-title="true" />
                </Transition>
                <Transition name="fade">
                    <users v-if="activeView === 'users'" :is-admin="isAdmin" :is-super-user="isSuperUser" :user-account="user" />
                </Transition>
                <Transition name="fade">
                    <license-report v-if="activeView === 'licenseReport'" :is-admin="isAdmin" />
                </Transition>
                <Transition name="fade">
                    <my-account v-if="activeView === 'userAccount'" :user="user" :companies="userCompanies" @change-organization="changeOrganzation" />
                </Transition>
                <Transition name="fade">
                    <ticket-system v-if="ticketEnabled && activeView === 'ticketSystem'" :is-observer="isObserver"
                                   :user="user" :feedback-guid="feedbackGuid" :has-company-ticket-url="ticketEnabled" />
                </Transition>
                <Transition name="fade">
                    <observation-groups v-if="activeView === 'observationGroups'"/>
                </Transition>
                <Transition name="fade">
                    <observation-types v-if="activeView === 'observationTypes'" />
                </Transition>
                <Transition name="fade">
                    <license-admin-view v-if="activeView === 'licenseAdminView'"/>
                </Transition>
                <Transition name="fade">
                    <companies v-if="activeView === 'companies'" />
                </Transition>
                <Transition name="fade">
                    <company-relationships v-if="activeView === 'companyRelationships'"/>
                </Transition>
                <Transition name="fade">
                    <material-types v-if="activeView === 'materialTypes'"/>
                </Transition>
                <Transition name="fade">
                    <external-contract-ids v-if="activeView === 'externalContractIds'"/>
                </Transition>
                <Transition name="fade">
                    <certificate-types v-if="activeView === 'certificateTypes'"/>
                </Transition>
                <Transition name="fade">
                    <pricing-models v-if="activeView === 'pricingModels'" />
                </Transition>
                <Transition name="fade">
                    <contract-trip-summary-generator v-if="activeView === 'contractTripSummaryGenerator'" />
                </Transition>
                <Transition name="fade">
                    <spreader-data-generator v-if="activeView === 'spreaderDataGenerator'" />
                </Transition>
                <Transition name="fade">
                    <work-assignments-user v-if="activeView === 'workAssignmentsUser'" :user="user"/>
                </Transition>
                <Transition name="fade">
                    <vehicles v-if="activeView === 'fleet'" :user="user"/>
                </Transition>
                <Transition name="fade">
                    <tracking-devices v-if="activeView === 'trackingDevices'"/>
                </Transition>
                <Transition name="fade">
                    <vehicle-defects v-if="activeView === 'fleetDefects'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <vehicle-allocation v-if="activeView === 'fleetAllocation'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <contracts v-if="activeView === 'contracts'" :user="user" :is-map-viewer="isMapViewer"
                               :isObserver="isObserver" :pre-selected-contract="shortcutItem" :pre-selected-tab="shortcutParam"/>
                </Transition>
                <Transition name="fade">
                    <contract-diaries v-if="activeView === 'workDiary'" :user="user" :is-map-viewer="isMapViewer" :is-observer="isObserver" :pre-selected-contract="shortcutItem" />
                </Transition>
                <Transition>
                    <contract-calendar-container v-if="activeView === 'calendar'" :user="user" />
                </Transition>
                <Transition>
                    <invoicing v-if="activeView === 'invoicing'" :user="user" :is-observer="isObserver" />
                </Transition>
                <Transition>
                    <work-time-view v-if="activeView === 'workTime'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <additional-costs v-if="activeView === 'additionalCosts'" :user="user" :is-observer="isObserver" :is-worker="isWorker" />
                </Transition>
                <Transition name="fade">
                    <users-certificates v-if="activeView === 'certificates'" />
                </Transition>
                <Transition name="fade">
                    <certificate-report v-if="activeView === 'certificateReport'" />
                </Transition>
                <Transition name="fade">
                    <area-defects v-if="activeView === 'areaDefects'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <areas v-if="activeView === 'areas'" :user="user" />
                </Transition>
                <Transition name="fade">
                    <api-keys v-if="activeView === 'apiKeys'" :is-super-user="isSuperUser"
                              :user-account="user" />
                </Transition>
                <Transition name="fade">
                    <area-reports v-if="activeView === 'areaReports'" :user-id="user.id" />
                </Transition>
                <Transition name="fade">
                    <area-equipment v-if="activeView === 'areaEquipment'" :is-observer="isObserver" />
                </Transition>
                <Transition name="fade">
                    <inspection-wizard v-if="activeView === 'inspectionWizard'" :user="user" :inspection-item="inspectionItem"/>
                </Transition>
                <Transition name="fade">
                    <observation-collections v-if="activeView === 'observationCollections'" />
                </Transition>
                <Transition name="fade">
                    <contract-collections v-if="activeView === 'contractCollections'" />
                </Transition>
                <Transition name="fade">
                    <contract-classes v-if="activeView === 'contractClasses'" :is-super-user="isSuperUser" />
                </Transition>
                <Transition name="fade">
                    <accounts v-if="activeView === 'accounts'" :is-super-user="isSuperUser" />
                </Transition>
                <Transition name="fade">
                    <subscriptions v-if="activeView === 'subscriptions'" :is-admin="isAdmin" />
                </Transition>
            </div>
        </div>
        <base-modal
            v-if="companyModal"
            :hide-header-close="!allowCloseCompanyModal"
            hide-footer
            :title="$t('common.select_company')"
            :prevent-close="true"
            @close="companyModal = false">
            <div class="col-sm-12">
                <div v-for="company in userCompanies" :key="company.id" class="company_selector" @click.stop="fetchCompanyToken(company.id)">
                    {{ company.name }}
                </div>
            </div>
        </base-modal>
        <!-- Common alert vew -->
        <RoutaAlert ref="alertRef"/>
    </div>
</template>
<script>
import Login from "./user/Login";
import ResetPasswordRequest from "./user/ResetPasswordRequest"
import SideMenu from "./SideMenu";
import About from "./About";
import {
    register as registerForNotifications,
    unregister as unregisterFromNotifications
} from "../modules/RoutaNotification";
import MapView from "./map/MapView";
import {userHelper} from "./mixins/UserMixin";
import {restApi} from "./mixins/RestApiMixin";
import BaseModal from "./BaseModal";
import TempMap from "./map/TempMap";
import Trips from "@/components/trip/Trips";
import ContractTripSummaries from "@/components/tripsummary/ContractTripSummaries";
import Deliveries from "@/components/materials/Deliveries";
import MaterialReports from "@/components/materials/report/MaterialReports";
import SpreaderData from "@/components/materials/spreaderdata/SpreaderData";
import ObservationsVideos from "@/components/observation/videos/ObservationVideos";
import Observations from "@/components/observation/Observations";
import ObservationReports from "@/components/observation/reports/ObservationReports";
import RoutaAlert from "@/components/utils/RoutaAlert";
import { ref, provide, onMounted, onBeforeUnmount, readonly, getCurrentInstance } from 'vue'
import StreetLightsView from "@/components/streetlights/StreetLightsView";
import MixedWasteBins from "@/components/mixedwastebins/MixedWasteBins";
import DangerReports from "@/components/dangerreports/DangerReports";
import UserAlerts from "@/components/user/alert/UserAlerts";
import InspectionsView from "@/components/inspections/InspectionsView";
import MvrInspectionsView from "@/components/inspections/mvrInspections/MvrInspectionsView";
import ContractorReports from "@/components/contractorreport/ContractorReports";
import ContractUserReports from "@/components/contractorreport/contractusers/ContractUserReport";
import SubcontractorApplications from "@/components/subcontractors/SubcontractorApplications";
import GeneralGuides from "@/components/documents/GeneralGuides";
import StormSewerLines from "@/components/stormsewers/StormSewerLines";
import StormSewerDrains from "@/components/stormsewers/StormSewerDrains";
import RoadSigns from "@/components/roadsign/RoadSigns.vue"
import ExternalScaleRecords from "@/components/materials/station/ExternalScaleRecords";
import MaterialDeliveryNotes from "@/components/materials/MaterialDeliveryNotes";
import MaterialStations from "@/components/materials/station/MaterialStations";
import WorkReports from "@/components/workreport/WorkReports.vue";
import WorkPermissions from "@/components/workpermission/WorkPermissions.vue";
import WorkAssignments from "@/components/workassignment/WorkAssignments.vue";
import MaterialStorageSearch from "@/components/materialstorage/MaterialStorageSearch.vue";
import Bridges from "@/components/bridge/Bridges.vue"
import Culverts from "@/components/culvert/Culverts.vue"
import AreaGroups from "@/components/area/group/AreaGroups.vue";
import AreaWork from "@/components/area/areawork/AreaWork.vue";
import NotificationLists from "@/components/notifications/NotificationLists";
import DataImporter from "@/components/dataimport/DataImporter";
import TaskTypes from "@/components/tasktype/TaskTypes";
import PriceLists from "@/components/pricingmodels/pricelists/PriceLists";
import CompanyDefaults from "@/components/settings/CompanyDefaults";
import ConversionModels from "@/components/pricingmodels/conversion/ConversionModels";
import Conversions from "@/components/settings/Conversions";
import Users from "@/components/user/Users";
import LicenseReport from "@/components/licensereport/LicenseReport";
import MyAccount from "@/components/user/MyAccount";
import TicketSystem from "@/components/louhi/TicketSystem";
import {utility} from "@/components/mixins/Utils";
import StormSewers from "@/components/stormsewers/StormSewers";
import ObservationGroups from "@/components/observation/group/ObservationGroups";
import ObservationTypes from "@/components/observation/type/ObservationTypes";
import LicenseAdminView from "@/components/licensereport/LicenseAdminView";
import Companies from "@/components/company/Companies";
import CompanyRelationships from "@/components/company/companyrelationship/CompanyRelationships";
import MaterialTypes from "@/components/materials/type/MaterialTypes";
import ExternalContractIds from "@/components/contract/external/ExternalContractIds";
import CertificateTypes from "@/components/certificate/CertificateTypes";
import PricingModels from "@/components/pricingmodels/PricingModels";
import ContractTripSummaryGenerator from "@/components/tripsummary/ContractTripSummaryGenerator";
import SpreaderDataGenerator from "@/components/materials/spreaderdata/SpreaderDataGenerator";
import WorkAssignmentsUser from "@/components/workassignment/WorkAssignmentsUser";
import Vehicles from "@/components/vehicle/Vehicles";
import TrackingDevices from "@/components/trackingdevices/TrackingDevices";
import VehicleDefects from "@/components/vehicle/defect/VehicleDefects";
import VehicleAllocation from "@/components/vehicle/allocation/VehicleAllocation";

import Contracts from "@/components/contract/Contracts.vue";
import ContractDiaries from "@/components/contractreport/ContractDiaries.vue";
import ContractCalendarContainer from "@/components/contractcalendar/ContractCalendarContainer.vue";
import Invoicing from "@/components/invoice/Invoicing.vue";
import WorkTimeView from "@/components/worktime/WorkTime.vue";
import AdditionalCosts from "@/components/additionalcost/AdditionalCosts.vue";
import UsersCertificates from "@/components/certificate/UsersCertificates.vue";
import CertificateReport from "@/components/certificate/CertificateReport";
import AreaDefects from "@/components/area/areadefect/AreaDefects.vue";
import Areas from "@/components/area/Areas.vue";
import ApiKeys from "@/components/apikeys/ApiKeys.vue";
import AreaReports from "@/components/area/areareport/AreaReports.vue";
import AreaEquipment from "@/components/area/areaequipment/AreaEquipment.vue";
import ObservationCollections from "@/components/observation/collection/ObservationCollections.vue";
import ContractCollections from "@/components/contract/collection/ContractCollections.vue";
import InspectionWizard from "@/components/inspections/wizard/InspectionWizard";
import Dispatch from "@/components/dispatch/Dispatch";
import Accounts from "@/components/account/Accounts.vue";
import ContractClasses from "@/components/contract/class/ContractClasses.vue";
import RoadReports from "./trip/roadreport/RoadReports.vue";
import FuelReport from "@/components/trip/fuelreport/FuelReport.vue";
import SpreaderDataTabbed from "./materials/spreaderdata/SpreaderDataTabbed.vue";
import RouteOptimizationWorkflow from "@/components/routeoptimization/RouteOptimizationWorkflow.vue";
import Subscriptions from "@/components/subscriptions/Subscriptions.vue";
import ToolBar from "@/components/ToolBar.vue";
import AiChatPanel from "@/components/ai/AIChat.vue";
import FavoritesPanel from "@/components/favorites/FavoritesPanel.vue";
import HelpPanel from "@/components/toolbar/HelpPanel.vue";
import AboutPanel from "@/components/About.vue";
import CostCenters from "./costcenter/CostCenters.vue";
import VideoRecordings from "@/components/videorecordings/VideoRecordings.vue"
export default {
    name: 'TempOffice',
    components: {
        CostCenters,
        ToolBar,
        AiChatPanel,
        HelpPanel,
        AboutPanel,
        FavoritesPanel,
        SpreaderDataTabbed,
        RouteOptimizationWorkflow,
        VideoRecordings,
        FuelReport,
        RoadReports,
        ContractClasses,
        Accounts,
        Dispatch,
        InspectionWizard,
        ContractCollections,
        ObservationCollections,
        ApiKeys,
        AreaDefects,
        Areas,
        AreaEquipment,
        AreaReports,
        CertificateReport,
        UsersCertificates,
        AdditionalCosts,
        WorkTimeView,
        Invoicing,
        ContractCalendarContainer,
        ContractDiaries,
        Contracts,
        WorkAssignments,
        WorkPermissions,
        WorkReports,
        VehicleAllocation,
        VehicleDefects,
        TrackingDevices,
        Vehicles,
        WorkAssignmentsUser,
        SpreaderDataGenerator,
        ContractTripSummaryGenerator,
        PricingModels,
        CertificateTypes,
        ExternalContractIds,
        MaterialTypes,
        CompanyRelationships,
        Companies,
        LicenseAdminView,
        ObservationTypes,
        ObservationGroups,
        StormSewers,
        TicketSystem,
        MyAccount,
        LicenseReport,
        Users,
        Conversions,
        ConversionModels,
        CompanyDefaults,
        PriceLists,
        TaskTypes,
        DataImporter,
        NotificationLists,
        MaterialStorageSearch,
        MaterialStations,
        MaterialDeliveryNotes,
        ExternalScaleRecords,
        GeneralGuides,
        SubcontractorApplications,
        ContractUserReports,
        ContractorReports,
        MvrInspectionsView,
        InspectionsView,
        UserAlerts,
        AreaGroups,
        AreaWork,
        DangerReports,
        StreetLightsView,
        MixedWasteBins,
        RoutaAlert,
        StormSewerLines,
        StormSewerDrains,
        Bridges,
        Culverts,
        RoadSigns,
        ObservationReports,
        Observations,
        ObservationsVideos,
        SpreaderData,
        MaterialReports,
        Deliveries,
        ContractTripSummaries,
        Subscriptions,
        Trips, TempMap, BaseModal, MapView, About, SideMenu, ResetPasswordRequest, Login},
    mixins: [userHelper, restApi, utility],
    data() {
        return {
            locale: this.getLanguage(),
            loggedIn: false,
            loginMessage: '',
            passwordReset: false,
            activeView: '',
            menuCollapsed: false,
            menuHidden: false,
            loginTime: null,
            user: null,
            feedbackGuid: null,
            userCompanies: [],
            isAdmin: false,
            isObserver: false,
            isWorker: false,
            isSuperUser: false,
            isMapViewer: false,
            logoutEvent: false,
            companyModal: false,
            ticketEnabled: true,
            allowCloseCompanyModal: false,
            menuItems: [],
            inspectionItem: null,
            shortcutItem: null,
            shortcutParam: null,
            activeTool: null
        }
    },
    setup() {
        // Showing messages for all components
        const alertRef = ref(null)
        const showAlert = (message, variant = 'danger') => {
            alertRef.value?.showAlert(message, variant)
        }
        provide('showAlert', showAlert)

        // Visibility state
        const appVisible = ref(!document.hidden)

        // Inactivity system
        const TIMEOUT = 30 * 60 * 1000
        let inactivityStartTime = null
        let inactivityTimer = null

        const { proxy } = getCurrentInstance()
        const logout = async () => {
            await proxy.logout()
        }

        const timedLogout = () => {
            alertRef.value?.showAlert(proxy.$t('common.timeout_logout'), 'danger')
            logout()
        }

        const restartInactivityTimer = () => {
            inactivityStartTime = new Date()
            if (inactivityTimer) {
                clearTimeout(inactivityTimer)
            }
            inactivityTimer = setTimeout(() => {
                timedLogout()
            }, TIMEOUT)
        }

        const handleVisibilityChange = () => {
            appVisible.value = !document.hidden
            if (!document.hidden) {
                const now = new Date()
                if (
                    inactivityStartTime &&
                    inactivityStartTime.getTime() + TIMEOUT < now.getTime()
                ) {
                    if (inactivityTimer) {
                        clearTimeout(inactivityTimer)
                    }
                    timedLogout()
                }
            }
        }

        const handleUserActivity = () => {
            restartInactivityTimer()
        }

        onMounted(() => {
            document.addEventListener('visibilitychange', handleVisibilityChange)
            document.body.addEventListener('click', handleUserActivity, true)
            restartInactivityTimer()
        })

        onBeforeUnmount(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            document.body.removeEventListener('click', handleUserActivity, true)
            if (inactivityTimer) {
                clearTimeout(inactivityTimer)
            }
        })

        provide('appVisible', appVisible)
        return { alertRef, showAlert }
    },
    computed: {
        mapZoom() {
            return this.$store.getters.mapZoomLevel || 12;
        },
        isChatEnabled() {
            return !!this.$store.state.initialized && this.$store.state.subscriptions.subscription?.chat?.is_active === true
        },
        appForeground() {
            return !document.hidden
        }
    },
    watch: {
        '$i18n.locale'() {
            if (this.loggedIn) {
                this.initMenuItems()
            }
        },
        '$store.state.subscriptions.subscription'() {
            if (this.loggedIn) {
                this.initMenuItems()
            }
        },
        loggedIn: function (value) {
            if (value === true) {
                this.loginMessage = null
                this.initUser()
            } else {
                this.loading = false
            }
        }
    },
    created() {
        this.$i18n.locale = this.locale
    },
    methods: {

        startInspection(viewName, inspectionType, inspectionItem) {
            this.inspectionItem = {
                ...inspectionItem,
                inspectionType: inspectionType
            }
            this.setActiveView(viewName)
        },

        showAlert(message, variant = 'danger') {
        },

        onToolChanged(tool) {
            if (tool === null && this.$refs.activePanel?.$refs.panel) {
                this.$refs.activePanel.$refs.panel.close()
            } else {
                this.activeTool = tool
            }
        },
        onToolClose() {
            setTimeout(() => { this.activeTool = null }, 250)
        },

        onMenuCollapsed(collapsed, hidden) {
            this.menuCollapsed = collapsed
            this.menuHidden = hidden
            this.$forceUpdate()
        },

        initUser: function () {
            this.loading = true
            this.restFetch(this.userInfoUrl, this.handleUserResponse)
        },

        handleUserResponse: function (response) {
            if (response && response.status === 200) {
                this.user = response.data
                // Check for user rights and prompt for company if necessary
                if (this.user.rights && this.user.rights.length > 0) {
                    // Resolve companies, for which user has roles
                    this.userCompanies = this.getUserCompanies()
                    if (this.userCompanies.length > 1) {
                        this.loading = false
                        this.allowCloseCompanyModal = false
                        this.companyModal = true
                    } else if (this.userCompanies.length === 1) {
                        // Get token for the only company we have here
                        this.fetchCompanyToken(this.userCompanies[0].id)
                    }
                } else {
                    // User info initialization failed - throw the user out with an error message
                    this.loading = false
                    this.loginFailed()
                }
            } else {
                // User info initialization failed - throw the user out with an error message
                this.loading = false
                this.loginFailed()
            }
        },

        initRoles: function () {
            if (this.user && this.user.roles) {
                if (this.hasAdmin(this.user.roles)) {
                    this.isAdmin = true
                    this.activeView = 'users'
                }
                if (this.hasObserver(this.user.roles)) {
                    this.isObserver = true
                    if (!this.activeView) {
                        this.activeView = 'map'
                    }
                }
                if (this.hasCompanySuperUser(this.user.roles)) {
                    this.isSuperUser = true
                    if (!this.activeView) {
                        this.activeView = 'users'
                    }
                }
                if (this.hasWorker(this.user.roles)) {
                    this.isWorker = true
                    if (!this.activeView) {
                        this.activeView = 'map'
                    }
                }
                if (this.hasMapViewer(this.user.roles)) {
                    this.isMapViewer = true
                    if (!this.activeView) {
                        this.activeView = 'map'
                    }
                }
                this.$store.commit('storeInitialized', true)
            }
        },

        initMenuItems() {
            this.menuItems = []
            const ticketsEnabled = this.user.api_key_types.ticket_system === true || import.meta.env.VITE_APP_GLOBAL_TICKET_URL !== undefined
            const subscription = this.$store.state.subscriptions.subscription
            if (this.isObserver) {
                this.menuItems =  !this.isSuperUser ? this.getSupervisorMenu(ticketsEnabled, subscription) : this.getCombinedSuperMenu(ticketsEnabled, subscription)
            } else if (this.isWorker) {
                this.menuItems = this.getWorkerMenu()
            } else if (this.isAdmin) {
                this.menuItems = this.getAdminMenu()
            } else if (this.isSuperUser) {
                this.menuItems = this.getSuperuserMenu()
            }
            if (this.isMapViewer) {
                this.menuItems = this.getMapViewerMenu()
            }
        },


        getUserCompanies: function () {
            let result = []
            if (this.user.rights && this.user.rights.length > 0) {
                this.user.rights.forEach(function (item) {
                    let existing = result.find(cmp => cmp.id === item.company.id)
                    if (!existing) {
                        result.push(item.company)
                    }
                })
            }
            // Sort by name in alphabetical order
            result.sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            });
            return result
        },

        async fetchCompanyToken(companyId) {
            this.loading = true
            this.companyModal = false
            this.restFetch(this.companyTokenUrl + '/' + companyId, this.handleCompanyTokenResponse)
        },

        handleCompanyTokenResponse: function (response) {
            this.loginTime = new Date()
            this.loading = false
            if (response && response.data && response.data.token) {
                this.$store.commit('storeToken', response.data.token)
                this.user = response.data.user
                this.resetRoles()
                this.initTicketEnabled()
                this.$store.dispatch('subscriptions/resetSubscription')
                this.$store.dispatch('routeOptimization/resetSetupState')
                this.$store.dispatch('subscriptions/getSubscription')
                this.initializeUI()
            } else {
                //this.loginFailed()
            }
        },

        resetRoles: function () {
            this.isAdmin = false
            this.isObserver = false
            this.isSuperUser = false
            this.isWorker = false
            this.isMapViewer = false
            this.hasAccess = false
        },

        loginFailed: function () {
            this.logout()
            this.showAlert(this.$t('login.init_fail'))
        },

        initTicketEnabled() {
            // TODO - Should this be in Sidebar menu? At least it should be informed
            this.ticketEnabled = this.user.api_key_types.ticket_system === true || import.meta.env.VITE_APP_GLOBAL_TICKET_URL !== undefined
        },

        initializeUI: function () {
            this.initRoles()
            this.initMenuItems()
            if (this.menuItems.length > 0) {
                this.startInactivityTimer()
                this.centerToCompany()
                this.$nextTick(() => {
                    this.$refs.menu.setActiveView(this.activeView)
                    if (this.$route.query.feedbackGuid && this.ticketEnabled) {
                        this.feedbackGuid = this.$route.query.feedbackGuid
                        this.activeView = 'ticketSystem'
                    }
                })
                //registerForNotifications(this, this.onMessage).catch(() => { });
            }
        },

        resetPasswordDone: function () {
            this.passwordReset = false
            this.showAlert(this.$t('login.reset_done'), 'info')
        },

        changeOrganzation() {
            this.companyModal = true
            this.allowCloseCompanyModal = true
        },

        setActiveView: function (viewName, item = null, param = null) {
            if (viewName !== this.activeView) {
                this.activeView = viewName
                this.shortcutItem = item
                this.shortcutParam = param
                if (this.shortcutItem) {
                    this.$refs.menu.setActiveView(this.activeView)
                }
            }
        },

        openShortcut(view, params) {
            this.shortcutItem = params.item
            this.shortcutParam = params.tab ?? null
            if (this.$refs.menu) this.$refs.menu.setActiveView(view)
            if (this.activeView === view) {
                this.activeView = null
                this.$nextTick(() => { this.activeView = view })
            } else {
                this.activeView = view
            }
        },


        async logout() {
            this.activeView = null
            this.loading = true
            this.logoutEvent = true
            await unregisterFromNotifications(this)
            this.$nextTick(() => {
                this.$store.dispatch('subscriptions/resetSubscription')
                this.$store.dispatch('routeOptimization/resetSetupState')
                this.$store.commit('storeToken', null)
                this.$store.commit('storeMapSettings', {}) // Default map settings
                this.loggedIn = false
                this.loading = false
                this.resetUserData()
                window.location.reload();
            })
        },

        resetUserData: function () {
            this.activeView = null
            this.user = null
            this.userCompanies = []
            this.$store.commit('storeInitialized', false)
        }

    }
}
</script>
<style scoped>
.routa-content {
    position: absolute;
    top: 0;
    bottom: 0;
    right: var(--toolbar-width);
    left: 200px;
    overflow: auto;
    background-color: #E8E8E8;
    z-index: 1 !important;
    transition: right 0.25s ease;
}

.routa-content > * {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
}

.large-content {
    left: 58px !important;
}

.full-content {
    left: 0 !important;
}

@media (max-width: 768px) {
    .routa-content {
        right: 0;
    }
}

@media (max-width: 420px) {
    .large-content {
        left: 58px !important;
    }

    .full-content {
        left: 0 !important;
    }
}
</style>
